import { useParams } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { unitId } from '@/annotations'
import { IconButton } from '@/components/IconButton'
import { Note } from '@/components/Note'
import { Chip } from '@/components/Chip'
import { Strip } from '@/components/Strip'
import { GridTable } from '@/components/GridTable'
import { getApiClient, ApiProblemError } from '@/data/apiClient'
import { UnexpectedRefusal, isUnexpectedRefusal } from '@/screens/auth-shared/Refusals'
import { useActiveOrgId, useActiveWorkspaceId } from '@/data/store'
import { JOB_STATUS_CHIP } from '@/screens/shared/jobFormatting'
import type { components } from '@/api/generated/schema'
import type { Problem } from '@/data/apiClient'

type SnapshotRepository = components['schemas']['SnapshotRepository']

/**
 * `v-firstbackup` — process/09-SCREEN-SPECS/v-firstbackup.md. **Reads the
 * snapshot MANIFEST, not the progress list** (rule 1, W109's own distinction) — a repository this
 * job has not yet reached is a real row here, not a missing one. `workspace.completeOnboarding`
 * fires from `ob-wz-6` (W142), not from this screen — this is only the screen that lands after it,
 * per this spec's own "Fired without a control" table, so no mutation is wired here at all.
 *
 * **The gap this doc comment used to report is closed**: `OnboardingLayer` is mounted (W153) and
 * its own `onCompleted` callback navigates here with a real `jobId` when `ob-wz-6`'s "Start my
 * first backup" actually started one. Correcting the record rather than leaving the earlier claim
 * standing.
 *
 * **`panel-header`/`grid-tools`/`grid-footer`/`strip` added W168** — a real "Refresh" `icon-button`
 * scoped to this screen's own query; a real footer note; the intro sentence promoted from a plain
 * `Note` to the shared `Strip`.
 *
 * **The status chip now uses the shared `JOB_STATUS_CHIP` (W172)**, replacing a local
 * `STATUS_VARIANT` map written against `Chip.tsx`'s old three-variant type — it mapped
 * `SUCCEEDED` to plain blue and `FAILED` to plain grey, both wrong once real state colours
 * existed to use. See `jobFormatting.ts`'s own doc comment for the prototype evidence.
 */
export function VFirstbackup() {
  const { jobId } = useParams<{ jobId: string }>()
  const orgId = useActiveOrgId()!
  const workspaceId = useActiveWorkspaceId()!
  const queryClient = useQueryClient()

  const repos = useQuery({
    queryKey: ['snapshot-repositories', orgId, workspaceId, jobId],
    // Found live, 2026-09-25 (systemic audit after VHealth.tsx/VDrift.tsx's own fix) — see that
    // fix's own doc comment for the mount-time/org-switch race this guards against.
    enabled: Boolean(orgId && workspaceId),
    queryFn: async () => {
      const client = getApiClient()
      const { data, error } = await client.GET('/orgs/{org_id}/workspaces/{workspace_id}/snapshots/{job_id}/repositories', {
        params: { path: { org_id: orgId, workspace_id: workspaceId, job_id: jobId! } },
      })
      if (error) throw new ApiProblemError(error as Problem)
      return data
    },
  })

  const problem = repos.error instanceof ApiProblemError ? repos.error.problem : undefined
  const rows = (repos.data?.items ?? []) as unknown as readonly SnapshotRepository[]

  return (
    <div data-scr={unitId('v-firstbackup')}>
      <h1 className="m-0 mb-3 text-section font-semibold">Building your baseline</h1>
      <Strip>This is your first capture — it reads what the snapshot holds, not a progress bar. A completed backup is not, by itself, proof that data can be restored (rule 2).</Strip>

      {problem && (isUnexpectedRefusal(problem.refusal_kind) ? <UnexpectedRefusal problem={problem} /> : <Note>{problem.title}</Note>)}

      <div data-cmp="panel" className="bg-panel border border-bd rounded-2 mt-6">
        <div data-cmp="panel-header" className="flex items-center gap-5 p-5 border-b border-bd font-semibold text-body-lg">
          <span className="flex-1">Repositories</span>
          <div data-cmp="grid-tools">
            <IconButton
              data-act={undefined}
              icon="i-refresh"
              label="Refresh"
              disabled={repos.isFetching}
              onClick={() => queryClient.invalidateQueries({ queryKey: ['snapshot-repositories', orgId, workspaceId, jobId] })}
            />
          </div>
        </div>
        <GridTable
          columns={[
            { key: 'repository', header: 'Repository', render: (r) => r.slug },
            { key: 'project', header: 'Project', render: (r) => <span className="text-muted">{r.project_key ?? '—'}</span> },
            {
              key: 'status',
              header: 'Status',
              render: (r) => <Chip variant={JOB_STATUS_CHIP[r.status] ?? 'plain'}>{r.status === 'PARTIAL_SUCCESS' ? 'Partial — did not reach everything' : r.status}</Chip>,
            },
            {
              key: 'pullRequests',
              header: 'Pull requests',
              render: (r) => (r.pull_requests_captured != null && r.pull_requests_total != null ? `${r.pull_requests_captured} of ${r.pull_requests_total}` : '—'),
            },
            { key: 'restorable', header: 'Restorable', render: (r) => (r.restorable ? 'Yes' : 'No') },
          ]}
          rows={rows}
          getRowKey={(r) => r.repository_id}
          emptyMessage={repos.isLoading ? '' : 'No repositories match these filters.'}
        />
        <div data-cmp="grid-footer" className="border-t border-bd p-5">
          <Note>Every repository in scope — not paged, not filtered.</Note>
        </div>
      </div>
    </div>
  )
}
